<!--
  @component NotificationContainer
  @description Container for toast notifications
-->
<script lang="ts">
  import { notifications, dismiss } from '@services/notificationService';
  import type { Notification } from '@types';

  // Get icon based on notification type
  function getIcon(type: Notification['type']): string {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
        return 'ℹ';
      default:
        return '';
    }
  }
</script>

{#if $notifications.length > 0}
  <div class="notifications-container" role="status" aria-live="polite">
    {#each $notifications as notification (notification.id)}
      <div class="notification notification--{notification.type}">
        <span class="notification-icon">{getIcon(notification.type)}</span>
        <span class="notification-message">{notification.message}</span>
        <button
          class="notification-close"
          on:click={() => dismiss(notification.id)}
          aria-label="Close notification"
        >
          ×
        </button>
      </div>
    {/each}
  </div>
{/if}

<style>
  .notifications-container {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 4000;
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-width: 400px;
    pointer-events: none;
  }

  .notification {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    box-shadow:
      0 4px 12px rgba(0, 0, 0, 0.3),
      0 0 0 1px rgba(255, 255, 255, 0.1);
    animation: notificationSlideIn 0.3s ease-out;
    pointer-events: auto;
    backdrop-filter: blur(8px);
  }

  .notification--success {
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
    color: white;
  }

  .notification--error {
    background: linear-gradient(135deg, #dc3545 0%, #e74c3c 100%);
    color: white;
  }

  .notification--warning {
    background: linear-gradient(135deg, #ffc107 0%, #fd7e14 100%);
    color: #333;
  }

  .notification--info {
    background: linear-gradient(135deg, #5c7cfa 0%, #748ffc 100%);
    color: white;
  }

  .notification-icon {
    font-size: 16px;
    font-weight: bold;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    flex-shrink: 0;
  }

  .notification-message {
    flex: 1;
    line-height: 1.4;
  }

  .notification-close {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: inherit;
    font-size: 18px;
    width: 24px;
    height: 24px;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s;
    flex-shrink: 0;
    padding: 0;
    line-height: 1;
  }

  .notification-close:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  .notification--warning .notification-close {
    color: #333;
  }

  @keyframes notificationSlideIn {
    from {
      opacity: 0;
      transform: translateX(100%) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateX(0) scale(1);
    }
  }

  /* Exit animation handled via CSS */
  .notification:global(.exiting) {
    animation: notificationSlideOut 0.3s ease-in forwards;
  }

  @keyframes notificationSlideOut {
    from {
      opacity: 1;
      transform: translateX(0);
    }
    to {
      opacity: 0;
      transform: translateX(100%);
    }
  }
</style>
